/**
 * Landing page header — mobile hamburger menu + hide-on-scroll behaviour.
 *
 * Simplified, single-level counterpart to the wombat/header block's
 * view.js (see src/blocks/header/view.js): same core mechanics (measured
 * height custom property, hamburger toggle, hide/reveal on scroll
 * direction) without the top-bar/megamenu/submenu handling that block
 * doesn't need here.
 *
 * @return {void}
 */
export const initHeaderLanding = () => {

	const header = document.querySelector( '.p-header' );

	if ( ! header ) {

		return;

	}

	const nav = header.querySelector( '.p-header__nav' );
	const hamburger = header.querySelector( '.js-header-hamburger' );

	/**
	 * Measure the header's rendered height into --header-height, used by
	 * the mobile nav panel to sit flush below it regardless of how tall
	 * the header actually renders (e.g. logo/menu wrapping).
	 *
	 * @return {void}
	 */
	const setHeaderHeight = () => {

		header.style.setProperty( '--header-height', `${ header.offsetHeight }px` );

	};

	setHeaderHeight();
	new ResizeObserver( setHeaderHeight ).observe( header );

	let lastScrollY = window.scrollY;
	let ticking = false;
	let menuScrollY = null;

	/**
	 * Close the mobile nav panel and reset the hamburger/body state.
	 *
	 * @return {void}
	 */
	const closeMobileNav = () => {

		header.classList.remove( 'p-header--menu-open' );
		document.body.classList.remove( 'p-header-lock-scroll' );

		if ( menuScrollY !== null ) {

			window.scrollTo( 0, menuScrollY );
			lastScrollY = menuScrollY;
			menuScrollY = null;

		}

		if ( hamburger ) {

			hamburger.setAttribute( 'aria-expanded', 'false' );

		}

	};

	if ( hamburger && nav ) {

		hamburger.addEventListener( 'click', () => {

			const isOpen = ! header.classList.contains( 'p-header--menu-open' );

			if ( isOpen ) {

				menuScrollY = window.scrollY;
				header.classList.add( 'p-header--menu-open' );
				header.classList.remove( 'p-header--nav-hidden' );
				hamburger.setAttribute( 'aria-expanded', 'true' );
				document.body.classList.add( 'p-header-lock-scroll' );
				window.scrollTo( 0, menuScrollY );

				nav.querySelector( 'a' )?.focus( { preventScroll: true } );

				return;

			}

			closeMobileNav();

		} );

		// Close on Escape, returning focus to the hamburger.
		document.addEventListener( 'keydown', ( event ) => {

			if ( event.key === 'Escape' && header.classList.contains( 'p-header--menu-open' ) ) {

				closeMobileNav();
				hamburger.focus();

			}

		} );

		// Close whenever a nav link is activated (in-page anchors don't
		// trigger a navigation that would otherwise dismiss the panel).
		nav.querySelectorAll( 'a' ).forEach( ( link ) => {

			link.addEventListener( 'click', closeMobileNav );

		} );

		// Close on outside click.
		document.addEventListener( 'click', ( event ) => {

			if ( header.classList.contains( 'p-header--menu-open' ) && ! header.contains( event.target ) ) {

				closeMobileNav();

			}

		} );

	}

	// Hide the header on scroll-down and reveal it on scroll-up, only
	// once the user has scrolled past the header itself, and never
	// while the mobile nav is open.

	window.addEventListener( 'scroll', () => {

		if ( ticking ) {

			return;

		}

		ticking = true;

		window.requestAnimationFrame( () => {

			const currentScrollY = window.scrollY;
			const scrolledPastHeader = currentScrollY > header.offsetHeight;
			const isMenuOpen = header.classList.contains( 'p-header--menu-open' );

			header.classList.toggle( 'p-header--stuck', currentScrollY > 0 );

			if ( scrolledPastHeader && ! isMenuOpen ) {

				header.classList.toggle( 'p-header--nav-hidden', currentScrollY > lastScrollY );

			} else {

				header.classList.remove( 'p-header--nav-hidden' );

			}

			lastScrollY = currentScrollY;
			ticking = false;

		} );

	}, { passive: true } );

};
